"use client";

import { useForm } from "@tanstack/react-form";
import { format } from "date-fns";
import { CalendarDays, ChevronLeft, Eye, Loader2, PenLine } from "lucide-react";
import { useRouter } from "next/navigation";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { SessionPreviewCard } from "@/components/activity-sessions/session-preview-card.client";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import type {
  ActivityCompetitionMode as ActivitySessionCompetitionMode,
  ActivitySessionFormat,
} from "@/db/schema/activities";
import type { ActivitySessionVisibility } from "@/db/schema/activity-session";
import { useActivityCatalog } from "@/hooks/use-activities";
import { useCreateActivitySessionMutation } from "@/hooks/use-activity-session";
import type { ActivityDefinition } from "@/lib/activities";
import {
  getActivitySessionRules,
  getDefaultSessionFormat,
  getSessionParticipantRange,
} from "@/lib/activity-config";
import { deriveActivitySessionCreatePricing } from "@/lib/activity-sessions/session-create";
import { uiHaptics } from "@/lib/ui-haptics";
import {
  type ActivitySessionValues,
  activitySessionSchema,
} from "@/validations/activity-session";
import { ActivityPickerStep } from "./activity-picker-step";
import type { Step } from "./activity-session-drawer.constants";
import {
  buildGeneratedSessionTitle,
  supportsSkillRequirements,
} from "./activity-session-drawer.utils";
import { EventDetailsStep } from "./event-details-step";
import { useActivityPicker } from "./use-activity-picker";

type ActivitySessionDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUser: {
    id: string;
    displayName: string;
    avatarUrl?: string | null;
  };
  onSuccess?: () => void;
  /**
   * Switch back to the regular post composer. Renders the "Post" half of the
   * Post / Plan toggle so the two flows stay reachable from each other.
   */
  onSwitchToPost?: () => void;
};

export function ActivitySessionDrawer({
  open,
  onOpenChange,
  currentUser,
  onSuccess,
  onSwitchToPost,
}: ActivitySessionDrawerProps) {
  const router = useRouter();
  const [step, setStep] = useState<Step>("select-activity");
  const [previewOpen, setPreviewOpen] = useState(false);
  const endTimeWasManual = useRef(false);

  const { mutateAsync: createActivitySession, isPending: isSubmitting } =
    useCreateActivitySessionMutation(currentUser);

  const getTomorrowNoon = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    return tomorrow;
  };
  const tomorrow = getTomorrowNoon();

  const form = useForm({
    defaultValues: {
      activityId: "",
      title: "",
      description: "",
      visibility: "buddies" as ActivitySessionVisibility,
      startDate: format(tomorrow, "yyyy-MM-dd"),
      startTime: "12:00",
      endTime: "13:00",
      locationName: "",
      address: "",
      latitude: 0,
      longitude: 0,
      minParticipants: 2,
      maxParticipants: 10,
      competitionMode: "casual" as ActivitySessionCompetitionMode,
      sessionFormat: null as ActivitySessionFormat | null,
      requiresApproval: false,
      pricingType: "free",
      pricePerPersonAmountMinor: 0,
      currency: "INR",
      venueBookingId: null,
      collectMoney: false,
      splitPricing: "divide_total",
      splitMode: "offline",
      splitAmountMinor: 0,
      organizerPlaying: true,
      extrasAllowed: false,
      extrasAllowancePercent: 50,
      extrasNote: "",
      minSkillLevel: null,
      maxSkillLevel: null,
    } as ActivitySessionValues,
    onSubmit: async ({ value }) => {
      const selectedActivity = activities.find(
        (activity) => activity.id === value.activityId,
      );
      const generatedTitle = buildGeneratedSessionTitle({
        activityName: selectedActivity?.name ?? "Activity",
        displayName: currentUser.displayName,
        startDate: value.startDate,
        startTime: value.startTime,
      });
      const result = activitySessionSchema.safeParse({
        ...value,
        title: generatedTitle,
        ...deriveActivitySessionCreatePricing(value),
      });

      if (!result.success) {
        toast.error(result.error.issues[0]?.message ?? "Invalid input");
        return;
      }

      try {
        const data = result.data;
        const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
        const endSameDay = new Date(`${data.startDate}T${data.endTime}`);
        const endDateTime =
          endSameDay.getTime() > startDateTime.getTime()
            ? endSameDay
            : new Date(endSameDay.getTime() + 24 * 60 * 60 * 1000);

        const res = await createActivitySession({
          activityId: data.activityId,
          title: data.title,
          description: data.description,
          visibility: data.visibility,
          startTime: startDateTime,
          endTime: endDateTime,
          locationName: data.locationName,
          address: data.address,
          latitude: data.latitude,
          longitude: data.longitude,
          pricingType: data.pricingType,
          pricePerPersonAmountMinor: data.pricePerPersonAmountMinor,
          currency: data.currency,
          minSkillLevel: data.minSkillLevel,
          maxSkillLevel: data.maxSkillLevel,
          minParticipants: data.minParticipants,
          maxParticipants: data.maxParticipants,
          competitionMode: data.competitionMode,
          sessionFormat: data.sessionFormat || undefined,
          requiresApproval: data.requiresApproval,
          venueBookingId: data.venueBookingId,
          split: data.collectMoney
            ? {
                mode: data.splitMode,
                pricing: data.splitPricing,
                totalAmountMinor:
                  data.splitPricing === "divide_total"
                    ? data.splitAmountMinor
                    : null,
                perPersonAmountMinor:
                  data.splitPricing === "fixed_per_person"
                    ? data.splitAmountMinor
                    : null,
                organizerPlaying: data.organizerPlaying,
                extrasAllowed: data.extrasAllowed,
                extrasAllowancePercent: data.extrasAllowancePercent,
                extrasNote: data.extrasNote.trim() || null,
              }
            : undefined,
        });

        toast.success("Session created. Invite others to join.");
        handleClose();
        onSuccess?.();
        router.push(`/sessions/${res.id}`);
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to create session",
        );
      }
    },
  });

  const { data: activities = [], isLoading: isLoadingActivities } =
    useActivityCatalog();

  const picker = useActivityPicker(activities);

  const handleSelectActivity = (activityId: ActivityDefinition["id"]) => {
    picker.rememberActivity(activityId);
    form.setFieldValue("activityId", activityId);
    const activity = activities.find((a) => a.id === activityId);
    if (activity) {
      form.setFieldValue(
        "title",
        buildGeneratedSessionTitle({
          activityName: activity.name,
          displayName: currentUser.displayName,
          startDate: form.state.values.startDate,
          startTime: form.state.values.startTime,
        }),
      );
    }
    const defaultSessionFormat = getDefaultSessionFormat(activity);
    const rules = getActivitySessionRules(activity);
    form.setFieldValue("sessionFormat", defaultSessionFormat);
    form.setFieldValue(
      "competitionMode",
      rules?.competition.defaultMode === "competitive"
        ? "competitive"
        : "casual",
    );
    const range = getSessionParticipantRange(activity, defaultSessionFormat);
    form.setFieldValue("minParticipants", range.min);
    form.setFieldValue("maxParticipants", range.max);
    if (!supportsSkillRequirements(activity, defaultSessionFormat)) {
      form.setFieldValue("minSkillLevel", null);
      form.setFieldValue("maxSkillLevel", null);
    }
    setStep("event-details");
  };

  const handleBack = () => {
    setStep("select-activity");
    form.setFieldValue("activityId", "");
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      form.reset();
      endTimeWasManual.current = false;
      setStep("select-activity");
      picker.reset();
    }, 300);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(val) => {
        if (!val) handleClose();
        else onOpenChange(true);
      }}
      modal
      // The handle is the only close; never mid-publish.
      dismissible={!isSubmitting}
    >
      <DrawerContent
        className="border-t border-border outline-none"
        style={{
          height: "calc(100dvh - env(safe-area-inset-top))",
          maxHeight: "calc(100dvh - env(safe-area-inset-top))",
        }}
      >
        <DrawerTitle className="sr-only">Plan activity</DrawerTitle>
        <div className="relative mx-auto flex h-full w-full max-w-lg flex-col">
          {/* Same frame as the post composer's header, so switching between
              the two segments doesn't move the title. Back goes back a step
              INSIDE the drawer; the handle is the only close. */}
          <header className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-border px-4 pt-1 pb-3">
            {step === "event-details" ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSubmitting}
                aria-label="Back to activities"
                className="-ml-2 text-text-secondary hover:text-text-primary dark:text-text-secondary dark:hover:text-text-primary"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            ) : (
              <span aria-hidden="true" />
            )}
            {/* Step two leads with its own activity hero, so the bar stays
                generic instead of naming the activity twice. */}
            <h2 className="truncate text-[17px] font-semibold text-text-primary">
              {step === "select-activity"
                ? "Choose an activity"
                : "New session"}
            </h2>
            <span aria-hidden="true" />
          </header>

          {onSwitchToPost && step === "select-activity" ? (
            <div className="border-b border-border px-4 py-2.5">
              <div className="flex w-full items-center gap-1 rounded-full bg-surface-alt p-1">
                <button
                  type="button"
                  onClick={() => {
                    uiHaptics.select();
                    onSwitchToPost();
                  }}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 text-sm font-semibold text-text-secondary transition hover:text-text-primary"
                >
                  <PenLine className="h-4 w-4" />
                  Post
                </button>
                <span className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-background py-2 text-sm font-semibold text-text-primary shadow-sm">
                  <CalendarDays className="h-4 w-4" />
                  Plan a session
                </span>
              </div>
            </div>
          ) : null}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div
              className={
                step === "select-activity"
                  ? "px-4 pt-3 pb-[max(env(safe-area-inset-bottom),1.5rem)]"
                  : "px-4 pt-5 pb-8"
              }
            >
              {step === "select-activity" && (
                <ActivityPickerStep
                  picker={picker}
                  isLoading={isLoadingActivities}
                  hasActivities={activities.length > 0}
                  onSelectActivity={handleSelectActivity}
                />
              )}

              {step === "event-details" && (
                <EventDetailsStep
                  form={form}
                  activities={activities}
                  currentUser={currentUser}
                  endTimeWasManual={endTimeWasManual}
                />
              )}
            </div>
          </div>

          {step === "event-details" ? (
            <div className="border-t border-border bg-background/95 px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.875rem)] backdrop-blur">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  disabled={isSubmitting}
                  className="h-12 flex-1 gap-1.5 rounded-full text-sm font-semibold"
                >
                  <Eye className="h-4 w-4" />
                  Preview
                </Button>
                <form.Subscribe selector={(state) => [state.canSubmit]}>
                  {([canSubmit]) => (
                    <Button
                      type="submit"
                      form="activity-session-form"
                      className="h-12 flex-[1.6] gap-2 rounded-full bg-brand text-base font-semibold text-white dark:bg-brand"
                      disabled={!canSubmit || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2
                            className="h-4 w-4 animate-spin"
                            aria-hidden="true"
                          />
                          Publishing…
                        </>
                      ) : (
                        "Publish session"
                      )}
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            </div>
          ) : null}
        </div>
      </DrawerContent>

      {/* Preview is opened on demand from the footer instead of pinned to the
          top of the form, so the form stays focused on input. */}
      <Drawer open={previewOpen} onOpenChange={setPreviewOpen} nested>
        <DrawerContent
          className="z-[110] max-h-[88dvh]"
          overlayClassName="z-[108]"
        >
          <DrawerTitle className="px-4 pt-2 pb-1 text-center font-semibold text-text-primary">
            Preview
          </DrawerTitle>
          <p className="px-4 pb-3 text-center text-xs text-text-tertiary">
            How this session will look in the feed.
          </p>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)]">
            <form.Subscribe selector={(state) => state.values}>
              {(values) => {
                const activity =
                  activities.find((item) => item.id === values.activityId) ??
                  null;
                const previewTitle =
                  values.title ||
                  buildGeneratedSessionTitle({
                    activityName: activity?.name ?? "Activity",
                    displayName: currentUser.displayName,
                    startDate: values.startDate,
                    startTime: values.startTime,
                  });
                return (
                  <SessionPreviewCard
                    values={values}
                    activity={activity}
                    title={previewTitle}
                    creator={currentUser}
                  />
                );
              }}
            </form.Subscribe>
          </div>
        </DrawerContent>
      </Drawer>
    </Drawer>
  );
}
